/**
 * Runtime selection: local | cloud | hybrid (cloud + IndexedDB fallback).
 *
 * Wire the real Worker:
 * ```js
 * localStorage.setItem("un8.media.config.v1", JSON.stringify({
 *   mode: "hybrid",
 *   cloud: {
 *     provider: "r2",
 * Production path: `api` adapter → Worker multipart → private R2.
 * Do not point at archived media-sign or any public bucket.
 *     publicBaseUrl: "https://media.un8.app",
 *     authToken: "<SIGN_SECRET if set>"
 *   }
 * }));
 * location.reload();
 * ```
 *
 * Local Worker: signUrl `http://127.0.0.1:8787/media/sign`
 * Or: window.__UN8_MEDIA__ = { signUrl, publicBaseUrl?, provider?, hybrid?, authToken? }
 */

import type { MediaUploadAdapter } from "./uploadAdapter";
import { localIndexedDbAdapter } from "./localIndexedDbAdapter";
import {
  createCloudUploadAdapter,
  type CloudUploadConfig,
} from "./cloudUploadAdapter";
import { createHybridUploadAdapter } from "./hybridUploadAdapter";
import { createApiUploadAdapter } from "./apiUploadAdapter";
import {
  setMediaUploadAdapter,
  getMediaUploadAdapter,
  getActiveMediaAdapterId,
} from "./uploadService";
import { apiHealth } from "../api/client";

const CONFIG_KEY = "un8.media.config.v1";

export type MediaBackendMode = "local" | "cloud" | "hybrid" | "api";

export interface MediaRuntimeConfig {
  mode: MediaBackendMode;
  cloud?: CloudUploadConfig;
}

declare global {
  interface Window {
    __UN8_MEDIA__?: {
      provider?: "s3" | "r2" | "supabase";
      signUrl: string;
      publicBaseUrl?: string;
      authToken?: string;
      /** default true when signUrl set */
      hybrid?: boolean;
    };
  }
}

export function defaultMediaConfig(): MediaRuntimeConfig {
  return { mode: "api" };
}

export function loadMediaConfig(): MediaRuntimeConfig {
  if (typeof window !== "undefined" && window.__UN8_MEDIA__?.signUrl) {
    const w = window.__UN8_MEDIA__;
    return {
      mode: w.hybrid === false ? "cloud" : "hybrid",
      cloud: {
        provider: w.provider ?? "r2",
        signUrl: w.signUrl,
        publicBaseUrl: w.publicBaseUrl,
        authToken: w.authToken,
      },
    };
  }

  try {
    const raw = localStorage.getItem(CONFIG_KEY);
    if (!raw) return defaultMediaConfig();
    const parsed = JSON.parse(raw) as MediaRuntimeConfig;
    if (
      parsed.mode !== "local" &&
      parsed.mode !== "cloud" &&
      parsed.mode !== "hybrid" &&
      parsed.mode !== "api"
    ) {
      return defaultMediaConfig();
    }
    return parsed;
  } catch {
    return defaultMediaConfig();
  }
}

export function saveMediaConfig(config: MediaRuntimeConfig): void {
  try {
    localStorage.setItem(CONFIG_KEY, JSON.stringify(config));
  } catch {
    /* quota */
  }
  applyMediaConfig(config);
}

/**
 * Convenience: point the app at a live (or local) sign Worker and activate hybrid.
 */
export function connectSignEndpoint(
  signUrl: string,
  options?: {
    publicBaseUrl?: string;
    authToken?: string;
    provider?: CloudUploadConfig["provider"];
    /** default "hybrid" */
    mode?: "cloud" | "hybrid";
  }
): MediaUploadAdapter {
  const config: MediaRuntimeConfig = {
    mode: options?.mode ?? "hybrid",
    cloud: {
      provider: options?.provider ?? "r2",
      signUrl,
      publicBaseUrl: options?.publicBaseUrl,
      authToken: options?.authToken,
    },
  };
  saveMediaConfig(config);
  return applyMediaConfig(config);
}

export function createAdapterFromConfig(config: MediaRuntimeConfig): MediaUploadAdapter {
  if (config.mode === "api") {
    return createHybridUploadAdapter(createApiUploadAdapter(), {
      onFallback: (err) => {
        console.warn("[UN8 media] API upload failed → IndexedDB fallback", err);
      },
    });
  }
  if ((config.mode === "cloud" || config.mode === "hybrid") && config.cloud?.signUrl) {
    const cloud = createCloudUploadAdapter(config.cloud);
    if (config.mode === "hybrid") {
      return createHybridUploadAdapter(cloud, {
        onFallback: (err) => {
          console.warn("[UN8 media] cloud/sign failed → IndexedDB fallback", err);
        },
      });
    }
    return cloud;
  }
  return localIndexedDbAdapter;
}

export function applyMediaConfig(
  config: MediaRuntimeConfig = loadMediaConfig()
): MediaUploadAdapter {
  const adapter = createAdapterFromConfig(config);
  setMediaUploadAdapter(adapter);
  return adapter;
}

export function initMediaUpload(override?: MediaRuntimeConfig): MediaUploadAdapter {
  const config = override ?? loadMediaConfig();
  const adapter = applyMediaConfig(config);
  // Prefer private API upload when /api is reachable; keep hybrid→IndexedDB fallback.
  void apiHealth().then((ok) => {
    if (ok && loadMediaConfig().mode === "api") {
      applyMediaConfig({ mode: "api" });
    } else if (!ok && loadMediaConfig().mode === "api") {
      // Stay on hybrid(api+local) — createAdapterFromConfig already wraps IndexedDB fallback.
      void adapter;
    }
  });
  return adapter;
}

export function getMediaBackendMode(): MediaBackendMode {
  const id = getActiveMediaAdapterId();
  if (id === "local-indexeddb") return "local";
  if (id === "hybrid") return "hybrid";
  if (id === "r2-private-api") return "api";
  return "cloud";
}

export { CONFIG_KEY as MEDIA_CONFIG_STORAGE_KEY, getMediaUploadAdapter };
